import React, { useEffect, useState } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Pencil } from "lucide-react";

const PLANS = ["starter", "business", "agency"];
const PERIODS = ["monthly", "yearly", "manual"];
const STATUSES = ["active", "cancelled"];

const PLAN_COLORS = {
    starter: "bg-blue-50 text-blue-700 border-blue-200",
    business: "bg-green-50 text-green-700 border-green-200",
    agency: "bg-purple-50 text-purple-700 border-purple-200",
};
const STATUS_COLORS = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
};

function PlanBadge({ plan }) {
    if (!plan) return <span className="text-gray-400 text-xs">–</span>;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${PLAN_COLORS[plan] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
            {plan.charAt(0).toUpperCase() + plan.slice(1)}
        </span>
    );
}

function StatusBadge({ status, t }) {
    if (!status) return <span className="text-gray-400 text-xs">–</span>;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
            {t(`admin_status_${status}`) || status}
        </span>
    );
}

export default function Admin() {
    const { t } = useI18n();
    const ctx = useOutletContext() || {};
    const subscription = ctx.subscription;

    const [merchants, setMerchants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ plan_id: "", billing_period: "", subscription_status: "" });
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/merchants");
            setMerchants(data);
        } catch {
            toast.error(t("toast_load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Redirect non-admins (after all hooks)
    if (subscription !== null && !subscription?.is_admin) {
        return <Navigate to="/app/withdrawals" replace />;
    }

    const openEdit = (m) => {
        setEditing(m);
        setForm({
            plan_id: m.plan_id || "none",
            billing_period: m.billing_period || "manual",
            subscription_status: m.subscription_status || "none",
        });
    };

    const save = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            await api.put(`/admin/merchants/${editing.id}/subscription`, {
                plan_id: form.plan_id === "none" ? null : form.plan_id,
                billing_period: form.billing_period === "none" ? null : form.billing_period,
                subscription_status: form.subscription_status === "none" ? null : form.subscription_status,
            });
            toast.success(t("toast_admin_updated"));
            setEditing(null);
            load();
        } catch {
            toast.error(t("toast_save_failed"));
        } finally {
            setSaving(false);
        }
    };

    const fmt = (iso) => {
        if (!iso) return "–";
        return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    const filtered = merchants.filter((m) => {
        const q = search.toLowerCase();
        return !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.company?.toLowerCase().includes(q);
    });

    return (
        <div data-testid="admin-page">
            <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="w-7 h-7 text-[#0b905d]" />
                <h1 className="font-display text-3xl font-bold tracking-tight text-[#00153f]">{t("admin_title")}</h1>
            </div>
            <p className="text-gray-600 mb-8">{t("admin_sub")}</p>

            {/* Search */}
            <div className="mb-5">
                <input
                    type="search"
                    placeholder={t("admin_search")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b905d]/30"
                />
            </div>

            {loading ? (
                <div className="text-sm text-gray-500">{t("loading")}</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-gray-500">{t("admin_no_merchants")}</div>
            ) : (
                <div className="border border-gray-200 rounded-md bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-gray-200 bg-gray-50">
                            <tr>
                                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_account")}</th>
                                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_plan")}</th>
                                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_status")}</th>
                                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_source")}</th>
                                <th className="text-center px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_shops")}</th>
                                <th className="text-center px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_withdrawals")}</th>
                                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500">{t("admin_col_registered")}</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map((m) => (
                                <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-[#00153f]">{m.name || "–"}</div>
                                        <div className="text-xs text-gray-500">{m.email}</div>
                                        {m.company && <div className="text-xs text-gray-400">{m.company}</div>}
                                    </td>
                                    <td className="px-4 py-3"><PlanBadge plan={m.plan_id} /></td>
                                    <td className="px-4 py-3"><StatusBadge status={m.subscription_status} t={t} /></td>
                                    <td className="px-4 py-3">
                                        {m.subscription_source ? (
                                            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${m.subscription_source === "manual" ? "bg-slate-100 text-slate-600" : "bg-indigo-50 text-indigo-600"}`}>
                                                {m.subscription_source}
                                            </span>
                                        ) : <span className="text-gray-400 text-xs">–</span>}
                                    </td>
                                    <td className="px-4 py-3 text-center font-mono text-gray-700">{m.shops_count}</td>
                                    <td className="px-4 py-3 text-center font-mono text-gray-700">{m.withdrawals_this_month}</td>
                                    <td className="px-4 py-3 text-gray-500">{fmt(m.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <Button size="sm" variant="outline" onClick={() => openEdit(m)} className="h-7 px-2">
                                            <Pencil className="w-3.5 h-3.5 mr-1" /> {t("admin_assign")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-display">{t("admin_assign_title")}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
                        {editing && (
                            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                                <div className="font-medium text-[#00153f]">{editing.name}</div>
                                <div className="text-xs text-gray-500">{editing.email}</div>
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-gray-500 block mb-1.5">{t("admin_col_plan")}</label>
                            <Select value={form.plan_id} onValueChange={(v) => setForm((f) => ({ ...f, plan_id: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t("admin_plan_none")}</SelectItem>
                                    {PLANS.map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-gray-500 block mb-1.5">{t("admin_col_status")}</label>
                            <Select value={form.subscription_status} onValueChange={(v) => setForm((f) => ({ ...f, subscription_status: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t("admin_plan_none")}</SelectItem>
                                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`admin_status_${s}`)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-gray-500 block mb-1.5">{t("admin_billing_period")}</label>
                            <Select value={form.billing_period} onValueChange={(v) => setForm((f) => ({ ...f, billing_period: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t("admin_plan_none")}</SelectItem>
                                    {PERIODS.map((p) => <SelectItem key={p} value={p}>{t(`admin_period_${p}`)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>{t("cancel")}</Button>
                        <Button onClick={save} disabled={saving} className="bg-[#0b905d] hover:bg-[#067a4d]">
                            {saving ? "..." : t("save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
